import GoogleMapReact from 'google-map-react';
import React, { useState, useRef } from 'react';
import { useDispatch } from 'store';
import useSupercluster from 'use-supercluster';

import Button from 'components/Button';

import Cluster from './child-components/Cluster';
import Marker from './child-components/Marker';
import { closeMap } from 'store/slices/search';

const URLKeys = { key: 'AIzaSyAMNm8f3xwhDSjb5POEEhWbWZ2sxxAKmYc' };

const degreesToRadians = (deg) => deg * (Math.PI / 180);

const radiansToDegrees = (rad) => rad * (180 / Math.PI);

const getBearing = (start, dest) => {
    // difference in longitudinal coordinates
    let dLng = degreesToRadians(dest.lng()) - degreesToRadians(start.lng());

    // difference in the phi of latitudinal coordinates
    const dPhi = Math.log(
        Math.tan(degreesToRadians(dest.lat()) / 2 + Math.PI / 4) /
            Math.tan(degreesToRadians(start.lat()) / 2 + Math.PI / 4),
    );

    // we need to recalculate dLon if it is greater than pi
    if (Math.abs(dLng) > Math.PI) {
        if (dLng > 0) {
            dLng = (2 * Math.PI - dLng) * -1;
        } else {
            dLng = 2 * Math.PI + dLng;
        }
    }

    // return the angle, normalized
    const bearing = (radiansToDegrees(Math.atan2(dLng, dPhi)) + 360) % 360;
    return bearing;
};

const Map = ({
    className,
    defaultZoom,
    defaultCenter,
    points,
    isInteractive,
    scrollTo,
    cluster = false,
    isMobile = false,
}) => {
    const mapRef = useRef();
    const apiRef = useRef();
    const dispatch = useDispatch();
    const containerRef = useRef();
    const [inTransition, setInTransition] = useState(false);
    const [bounds, setBounds] = useState(defaultCenter);
    const [zoom, setZoom] = useState(defaultZoom);

    const { clusters, supercluster } = useSupercluster({
        points,
        bounds,
        zoom,
        options: { radius: 125, maxZoom: 15, minPoints: cluster ? 2 : 9999 },
    });

    const [activeMarker, setActiveMarker] = useState({ id: null });

    const getMapBounds = (map, maps, p) => {
        const b = new maps.LatLngBounds();

        p.forEach((point) => {
            const [lng, lat] = point.geometry.coordinates;
            b.extend(new maps.LatLng(lat, lng));
        });

        return b;
    };

    const bindResizeListener = (map, maps, b) => {
        maps.event.addDomListenerOnce(map, 'idle', () => {
            maps.event.addDomListener(window, 'resize', () => {
                map.fitBounds(b);
            });
        });
    };

    const apiIsLoaded = (map, maps, p) => {
        if (cluster) {
            // Get bounds by our places
            // eslint-disable-next-line no-shadow
            const bounds = getMapBounds(map, maps, p);
            // Fit map to bounds
            map.fitBounds(bounds);
            // Bind the resize listener
            bindResizeListener(map, maps, bounds);
        }
    };

    const computePaddedBounds = (npad, spad, epad, wpad) => {
        const SW = mapRef.current.getBounds().getSouthWest();
        const NE = mapRef.current.getBounds().getNorthEast();
        const topRight = mapRef.current.getProjection().fromLatLngToPoint(NE);
        const bottomLeft = mapRef.current.getProjection().fromLatLngToPoint(SW);
        // eslint-disable-next-line no-restricted-properties
        const scale = Math.pow(2, mapRef.current.getZoom());

        const SWtopoint = mapRef.current.getProjection().fromLatLngToPoint(SW);
        const SWpoint = new apiRef.current.Point(
            (SWtopoint.x - bottomLeft.x) * scale + wpad,
            (SWtopoint.y - topRight.y) * scale - spad,
        );
        const SWworld = new apiRef.current.Point(
            SWpoint.x / scale + bottomLeft.x,
            SWpoint.y / scale + topRight.y,
        );
        const pt1 = mapRef.current.getProjection().fromPointToLatLng(SWworld);

        const NEtopoint = mapRef.current.getProjection().fromLatLngToPoint(NE);
        const NEpoint = new apiRef.current.Point(
            (NEtopoint.x - bottomLeft.x) * scale - epad,
            (NEtopoint.y - topRight.y) * scale + npad,
        );
        const NEworld = new apiRef.current.Point(
            NEpoint.x / scale + bottomLeft.x,
            NEpoint.y / scale + topRight.y,
        );
        const pt2 = mapRef.current.getProjection().fromPointToLatLng(NEworld);

        return new apiRef.current.LatLngBounds(pt1, pt2);
    };

    const onMarkerMouseEnter = (lat, lng) => {
        const b = computePaddedBounds(115, 115, 115, 115);
        const point = new apiRef.current.LatLng(lat, lng);
        if (!b.contains(point)) {
            setInTransition(true);
            const boundsCenter = b.getCenter();
            const bearing =
                Math.abs(getBearing(boundsCenter, point)) < 180 ? 'ne' : 'sw';
            const destinationBounds =
                bearing === 'ne'
                    ? new apiRef.current.LatLngBounds(boundsCenter, point)
                    : new apiRef.current.LatLngBounds(point, boundsCenter);

            const center = destinationBounds.getCenter();
            setTimeout(() => {
                mapRef.current.panTo(center);
            }, 75);
        }
    };

    return (
        <div className={className} ref={containerRef}>
            <GoogleMapReact
                bootstrapURLKeys={URLKeys}
                defaultCenter={defaultCenter}
                defaultZoom={defaultZoom}
                options={() => ({
                    scrollwheel: false,
                    zoomControl: !isMobile,
                    mapTypeControl: !isMobile,
                    scaleControl: !isMobile,
                    streetViewControl: !isMobile,
                    fullscreenControl: false,
                })}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => {
                    mapRef.current = map;
                    apiRef.current = maps;
                    apiIsLoaded(map, maps, points);
                    if (scrollTo) {
                        containerRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        });
                    }
                }}
                // eslint-disable-next-line no-shadow
                onChange={({ zoom, bounds }) => {
                    setZoom(zoom);
                    setBounds([
                        bounds.nw.lng,
                        bounds.se.lat,
                        bounds.se.lng,
                        bounds.nw.lat,
                    ]);
                }}
            >
                {
                    // eslint-disable-next-line no-shadow
                    clusters.map((cluster) => {
                        const [
                            longitude,
                            latitude,
                        ] = cluster.geometry.coordinates;
                        const {
                            cluster: isCluster,
                            point_count: pointCount,
                        } = cluster.properties;

                        if (isCluster) {
                            return (
                                <Cluster
                                    key={`cluster-${cluster.id}`}
                                    lat={latitude}
                                    lng={longitude}
                                    style={{
                                        width: `${
                                            30 +
                                            (pointCount / points.length) * 25
                                        }px`,
                                        height: `${
                                            30 +
                                            (pointCount / points.length) * 25
                                        }px`,
                                    }}
                                    onClick={() => {
                                        const expansionZoom = Math.min(
                                            supercluster.getClusterExpansionZoom(
                                                cluster.id,
                                            ),
                                            20,
                                        );
                                        mapRef.current.setZoom(expansionZoom);
                                        mapRef.current.panTo({
                                            lat: latitude,
                                            lng: longitude,
                                        });
                                    }}
                                    pointCount={pointCount}
                                />
                            );
                        }

                        return (
                            <Marker
                                key={`hotel-${cluster.properties.id}`}
                                lat={latitude}
                                lng={longitude}
                                {...cluster.properties}
                                isActiveMarker={
                                    cluster.properties.id === activeMarker.id ||
                                    false
                                }
                                onMarkerSelect={() => {
                                    if (isInteractive) {
                                        onMarkerMouseEnter(latitude, longitude);
                                        // eslint-disable-next-line object-curly-newline
                                        setActiveMarker({
                                            id: cluster.properties.id,
                                            // eslint-disable-next-line object-curly-newline
                                        });
                                    }
                                }}
                                onMarkerDismiss={(e) => {
                                    e.stopPropagation();
                                    if (isInteractive) {
                                        setInTransition(false);
                                        setActiveMarker({ id: null });
                                    }
                                }}
                                isInTransition={inTransition}
                            />
                        );
                    })
                }
            </GoogleMapReact>
            <Button
                className="modal__close"
                type="default"
                action={() => {
                    dispatch(closeMap());
                }}
            />
        </div>
    );
};

export default Map;
